import { RuntsCerca } from "@/components/RuntsCerca";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, FileText, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/pages/cauzioni/cauApi";
import { api as hubApi } from "@/lib/api";
import CauShell from "@/pages/cauzioni/CauShell";
import { EsitoBadge, StatoBadge } from "@/pages/cauzioni/Badges";
import { euro, TIPI_GARANZIA, FORME_GIURIDICHE, ESITO, STATO } from "@/pages/cauzioni/labels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const EMPTY = {
  cliente: { ragioneSociale: "", partitaIva: "", codiceFiscale: "", formaGiuridica: "", settore: "", sitoWeb: "" },
  tipoGaranzia: "altra_cauzione", oggettoGaranzia: "", enteBeneficiario: "",
  importo: "", durataMesi: "", premioAnnuale: "", anagraficaId: null,
};

function NewPraticaDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [anaOpen, setAnaOpen] = useState(false);
  const [anaResults, setAnaResults] = useState([]);

  const searchAna = async (q) => {
    if (!q || q.length < 2) { setAnaResults([]); return; }
    try { const { data } = await hubApi.get("/anagrafiche", { params: { q, limit: 15 } }); setAnaResults(data); }
    catch { setAnaResults([]); }
  };

  const pickAna = (a) => {
    setF((prev) => ({
      ...prev, anagraficaId: a.id,
      cliente: {
        ...prev.cliente, ragioneSociale: a.ragione_sociale || "", partitaIva: a.partita_iva || "",
        codiceFiscale: a.codice_fiscale || "", formaGiuridica: a.forma_giuridica || prev.cliente.formaGiuridica,
        settore: a.tipologia_ente || prev.cliente.settore,
        sitoWeb: a.sito_web || prev.cliente.sitoWeb || "",
      },
    }));
    setAnaOpen(false);
    toast.success("Anagrafica collegata dall'archivio condiviso");
  };

  const create = async () => {
    if (!f.cliente.ragioneSociale.trim() || (!f.cliente.partitaIva.trim() && !f.cliente.codiceFiscale.trim())) {
      toast.error("Ragione sociale e almeno uno tra P.IVA / Codice fiscale sono obbligatori"); return;
    }
    setBusy(true);
    try {
      const payload = { ...f, importo: Number(f.importo) || 0, durataMesi: Number(f.durataMesi) || 0, premioAnnuale: Number(f.premioAnnuale) || 0 };
      const { data } = await api.post("/pratiche", payload);
      toast.success("Pratica creata"); setOpen(false); setF(EMPTY); onCreated(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const [lk, setLk] = useState(null);
  const lookup = async () => {
    const piva = f.cliente.partitaIva.replace(/\D/g, "");
    if (piva.length !== 11) { toast.error("Inserisci una P.IVA di 11 cifre"); return; }
    try {
      const { data } = await hubApi.get("/ai/lookup-piva", { params: { piva } });
      setLk(data);
      if (data.runts) setF((prev) => ({ ...prev, cliente: { ...prev.cliente, codiceFiscale: data.runts.codice_fiscale, runtsIscritto: true } }));
      if (!data.disponibile) { toast.error(data.motivo || "Registro Imprese non disponibile"); return; }
      const FORME = { SR: "S.r.l.", RS: "S.r.l.s.", SP: "S.p.A.", SN: "S.n.c.", AS: "S.a.s.", DI: "Ditta individuale", SC: "Cooperativa", CS: "Cooperativa sociale", FO: "Fondazione", AN: "Associazione (APS/ODV/ETS)", AA: "Associazione (APS/ODV/ETS)", CO: "Consorzio", CC: "Consorzio" };
      const forma = FORME[(data.natura_giuridica || "").toUpperCase()];
      setF((prev) => ({ ...prev, cliente: { ...prev.cliente, ragioneSociale: data.ragione_sociale || prev.cliente.ragioneSociale, formaGiuridica: forma || prev.cliente.formaGiuridica } }));
      toast.success(`Dati compilati da ${data.runts ? "RUNTS (Terzo Settore)" : "Registro Imprese"}`);
    } catch { toast.error("Verifica non riuscita"); }
  };
  const upd = (k, v) => setF({ ...f, [k]: v });
  const updC = (k, v) => setF({ ...f, cliente: { ...f.cliente, [k]: v } });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="new-pratica-button"><Plus size={16} className="mr-1.5" /> Nuova pratica</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">Nuova pratica di cauzione</DialogTitle>
          <DialogDescription>Collega un'anagrafica esistente o inserisci i dati del cliente.</DialogDescription>
        </DialogHeader>

        <div className="mb-2">
          <label className="text-sm font-medium text-slate-700">Collega da anagrafica condivisa</label>
          <Popover open={anaOpen} onOpenChange={setAnaOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" role="combobox" data-testid="np-anagrafica-picker" className="mt-1 w-full justify-between font-normal">
                {f.anagraficaId ? f.cliente.ragioneSociale : "Cerca un ente nell'archivio…"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="p-0 w-[--radix-popover-trigger-width]" align="start">
              <Command shouldFilter={false}>
                <CommandInput placeholder="Digita ragione sociale o P.IVA…" onValueChange={searchAna} data-testid="np-anagrafica-search" />
                <CommandList>
                  <CommandEmpty>Nessuna anagrafica trovata.</CommandEmpty>
                  <CommandGroup>
                    {anaResults.map((a) => (
                      <CommandItem key={a.id} value={a.id} onSelect={() => pickAna(a)} data-testid={`np-ana-opt-${a.id}`}>
                        <div><div className="text-sm">{a.ragione_sociale}</div><div className="text-xs text-slate-400">{a.partita_iva || a.codice_fiscale || ""}</div></div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        <div className="grid grid-cols-2 gap-4 py-2">
          <div className="col-span-2 space-y-1.5">
            <label className="text-sm font-medium text-slate-700">Ragione sociale *</label>
            <Input value={f.cliente.ragioneSociale} onChange={(e) => updC("ragioneSociale", e.target.value)} data-testid="np-ragione-sociale" />
          </div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Partita IVA</label>
            <div className="flex gap-2"><Input value={f.cliente.partitaIva} onChange={(e) => { updC("partitaIva", e.target.value); setLk(null); }} data-testid="np-piva" />
              <Button type="button" variant="outline" className="rounded-sm shrink-0" onClick={lookup} data-testid="np-piva-lookup">Verifica</Button></div>
            {lk?.disponibile && <p className="text-[11px] text-emerald-700" data-testid="np-piva-esito">{lk.ragione_sociale} · {lk.comune} · forma giuridica {lk.natura_giuridica} ({lk.runts ? "RUNTS" : "Registro Imprese"})</p>}</div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Codice fiscale</label>
            <Input value={f.cliente.codiceFiscale} onChange={(e) => updC("codiceFiscale", e.target.value)} data-testid="np-cf" />
            <RuntsCerca onScegli={(e) => setF((prev) => ({ ...prev, cliente: { ...prev.cliente, ragioneSociale: e.denominazione, codiceFiscale: e.codice_fiscale, runtsIscritto: true, settore: prev.cliente.settore || e.sezione_tc } }))} label="Cerca ETS nel RUNTS" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Forma giuridica</label>
            <Select value={f.cliente.formaGiuridica} onValueChange={(v) => updC("formaGiuridica", v)}>
              <SelectTrigger data-testid="np-forma-giuridica"><SelectValue placeholder="Seleziona" /></SelectTrigger>
              <SelectContent>{FORME_GIURIDICHE.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Settore</label>
            <Input value={f.cliente.settore} onChange={(e) => updC("settore", e.target.value)} data-testid="np-settore" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Sito web</label>
            <Input value={f.cliente.sitoWeb} onChange={(e) => updC("sitoWeb", e.target.value)} placeholder="https://www.cliente.it" data-testid="np-sitoweb" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Tipo garanzia</label>
            <Select value={f.tipoGaranzia} onValueChange={(v) => upd("tipoGaranzia", v)}>
              <SelectTrigger data-testid="np-tipo-garanzia"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(TIPI_GARANZIA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
            </Select></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Importo (€)</label>
            <Input type="number" value={f.importo} onChange={(e) => upd("importo", e.target.value)} data-testid="np-importo" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Durata (mesi)</label>
            <Input type="number" value={f.durataMesi} onChange={(e) => upd("durataMesi", e.target.value)} data-testid="np-durata" /></div>
          <div className="space-y-1.5"><label className="text-sm font-medium text-slate-700">Premio annuale (€)</label>
            <Input type="number" value={f.premioAnnuale} onChange={(e) => upd("premioAnnuale", e.target.value)} data-testid="np-premio" /></div>
        </div>
        <DialogFooter>
          <Button onClick={create} disabled={busy} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="np-create-button">{busy ? "Creazione…" : "Crea pratica"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, accent = "text-slate-900", small }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm px-4 py-3">
      <div className="text-[11px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`font-display font-extrabold ${small ? "text-lg" : "text-2xl"} ${accent}`}>{value}</div>
    </div>
  );
}

export default function CauDashboard() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [statoFilter, setStatoFilter] = useState("all");
  const [esitoFilter, setEsitoFilter] = useState("all");

  const load = () => api.get("/pratiche").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/pratiche/${id}`); toast.success("Pratica eliminata"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const all = items || [];
  const filtered = all.filter((p) => {
    const okQ = !q || (p.cliente?.ragioneSociale || "").toLowerCase().includes(q.toLowerCase()) || (p.cliente?.partitaIva || "").includes(q);
    const okStato = statoFilter === "all" || p.statoPratica === statoFilter;
    const okEsito = esitoFilter === "all" || p.esitoFinale === esitoFilter || (esitoFilter === "none" && !p.esitoFinale);
    return okQ && okStato && okEsito;
  });

  const stats = {
    totale: all.length,
    proponibili: all.filter((p) => p.esitoFinale === "proponibile").length,
    mitigazioni: all.filter((p) => p.esitoFinale === "proponibile_con_mitigazioni").length,
    nonProponibili: all.filter((p) => p.esitoFinale === "non_proponibile").length,
    importo: all.reduce((s, p) => s + (Number(p.importo) || 0), 0),
  };
  const scored = all.filter((p) => p.scoreTotale != null);
  const scoreMedio = scored.length ? Math.round(scored.reduce((s, p) => s + p.scoreTotale, 0) / scored.length) : null;

  return (
    <CauShell>
      <div className="mx-auto max-w-[1400px] px-6 py-8" data-testid="cau-dashboard-page">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Pratiche</h1>
            <p className="text-sm text-slate-500 mt-1">Istruttoria assuntiva e relazioni fideiussorie.</p>
          </div>
          <NewPraticaDialog onCreated={(p) => nav(`/cauzioni-pro/pratica/${p.id}`)}
            autoOpen={sp.get("nuovo") === "1"} preAnaId={sp.get("ente")} preNome={sp.get("nome")} />
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-6" data-testid="stats-row">
          <StatCard label="Pratiche totali" value={stats.totale} />
          <StatCard label="Proponibili" value={stats.proponibili} accent="text-emerald-600" />
          <StatCard label="Con mitigazioni" value={stats.mitigazioni} accent="text-amber-600" />
          <StatCard label="Non proponibili" value={stats.nonProponibili} accent="text-rose-600" />
          <StatCard label="Score medio" value={scoreMedio != null ? `${scoreMedio}` : "—"} />
          <StatCard label="Importo totale" value={euro(stats.importo)} small />
        </div>

        <div className="bg-white border border-slate-200 rounded-sm">
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-center gap-2 flex-1">
              <Search size={16} className="text-slate-400" />
              <input placeholder="Cerca per ragione sociale o P.IVA…" value={q} onChange={(e) => setQ(e.target.value)} className="w-full text-sm outline-none bg-transparent" data-testid="search-input" />
            </div>
            <div className="flex items-center gap-2">
              <Select value={statoFilter} onValueChange={setStatoFilter}>
                <SelectTrigger className="h-9 w-[150px] text-sm" data-testid="filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  {Object.entries(STATO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={esitoFilter} onValueChange={setEsitoFilter}>
                <SelectTrigger className="h-9 w-[180px] text-sm" data-testid="filter-esito"><SelectValue placeholder="Esito" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli esiti</SelectItem>
                  {Object.entries(ESITO).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  <SelectItem value="none">Da valutare</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {items === null ? (
            <div className="p-12 text-center text-slate-400">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-16 text-center">
              <FileText className="mx-auto text-slate-300 mb-3" size={40} />
              <p className="text-slate-500">{all.length === 0 ? "Nessuna pratica. Crea la prima pratica per iniziare l'istruttoria." : "Nessuna pratica corrisponde ai filtri selezionati."}</p>
            </div>
          ) : (
            <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="pratiche-table">
              <thead>
                <tr className="text-left text-slate-400 text-xs uppercase tracking-wide border-b border-slate-200">
                  <th className="px-4 py-3 font-medium">Cliente</th>
                  <th className="px-4 py-3 font-medium">P.IVA / CF</th>
                  <th className="px-4 py-3 font-medium text-right">Importo</th>
                  <th className="px-4 py-3 font-medium text-right">Durata</th>
                  <th className="px-4 py-3 font-medium text-right">Score</th>
                  <th className="px-4 py-3 font-medium">Stato</th>
                  <th className="px-4 py-3 font-medium">Esito</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors" onClick={() => nav(`/cauzioni-pro/pratica/${p.id}`)} data-testid={`pratica-row-${p.id}`}>
                    <td className="px-4 py-3 font-medium text-slate-900">{p.cliente?.ragioneSociale || "—"}</td>
                    <td className="px-4 py-3 text-slate-500">{p.cliente?.partitaIva || p.cliente?.codiceFiscale || "—"}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-700">{euro(p.importo)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-slate-500">{p.durataMesi || 0} m</td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">{p.scoreTotale != null ? `${p.scoreTotale}` : "—"}</td>
                    <td className="px-4 py-3"><StatoBadge stato={p.statoPratica} /></td>
                    <td className="px-4 py-3"><EsitoBadge esito={p.esitoFinale} /></td>
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="text-slate-400 hover:text-rose-600 p-1" data-testid={`delete-pratica-${p.id}`}><Trash2 size={16} /></button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Eliminare la pratica?</AlertDialogTitle>
                            <AlertDialogDescription>L'azione è irreversibile e rimuove tutti i dati associati.</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Annulla</AlertDialogCancel>
                            <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={() => del(p.id)} data-testid={`confirm-delete-${p.id}`}>Elimina</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
        </div>
      </div>
    </CauShell>
  );
}
